<script lang="ts">
	import { Image } from "@unpic/svelte";
	import { Heading } from "flowbite-svelte-v1";
	import { getContext } from "svelte";
	import type { LandingPage } from "#gen/bundles/v1/bundle_pb.js";
	import Icon from "#lib/components/Icon.svelte";
	import P from "#lib/components/P.svelte";
	import TokenizedAvatar from "#lib/components/TokenizedAvatar.svelte";
	import TokenizedText from "#lib/components/TokenizedText.svelte";
	import type { LayoutProps } from "#lib/components/flow/viewer/layout/types.js";
	import { APP_ANCHOR_NAME, CONVERSION_ANCHOR_NAME, SECTION_1_ANCHOR_NAME, SECTION_2_ANCHOR_NAME } from "#lib/dag.js";
	import { transformURL } from "#lib/image.js";
	import { getHeaderBackgroundImage } from "#lib/theme.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Writable } from "svelte/store";

	const tokenProvider: Writable<TokenProvider> = getContext("tokenProvider");
	const { container: landingPage, mode, style, share, section1, section2, conversion, apps, accessory, footer }: LayoutProps<LandingPage> = $props();
	const headerBackgroundImage = $derived(transformURL(getHeaderBackgroundImage(landingPage)));
</script>

<div class="flex h-full w-full flex-col justify-start" {style}>
	<div class="relative flex-none overflow-clip" style="color: var(--header-color);">
		<div class="absolute inset-0" style="background-color: var(--header-background-color)">
			{#if headerBackgroundImage}
				<Image src={headerBackgroundImage} height={320} width={480} layout="constrained" priority={true} style="height: 100%; max-height: 100%;" />
			{/if}
		</div>
		<div class="relative pt-4">
			{@render share?.()}
		</div>
		<div class="relative w-full px-4 pt-8 pb-3">
			<div class="flex min-h-32 grow flex-col gap-2">
				<Heading tag="h1" style="color: var(--header-color);" class="text-2xl leading-tight font-semibold">
					<TokenizedText provider={$tokenProvider} text={landingPage.primaryText} {mode} />
				</Heading>
				<div class="flex min-h-36 grow-0 flex-col justify-between gap-12 pr-42">
					<div>
						{#if landingPage.secondaryText}
							<P size="base" color="" class="ellipsis pt-1 leading-tight">
								{#if landingPage.secondaryText.icon}
									<Icon iconSet={landingPage.secondaryText.iconSet} name={landingPage.secondaryText.icon} />
								{/if}
								<TokenizedText provider={$tokenProvider} text={landingPage.secondaryText.text} {mode} />
							</P>
						{/if}
						{#each landingPage.tertiaryText as item, i (i)}
							<P secondary size="base" color="" class="ellipsis pt-1 leading-tight opacity-50">
								{#if item.icon}
									<Icon iconSet={item.iconSet} name={item.icon} />
								{/if}
								<TokenizedText provider={$tokenProvider} text={item.text} {mode} />
							</P>
						{/each}
					</div>
					<div class="relative">
						{@render section1?.()}
						{@render accessory?.[SECTION_1_ANCHOR_NAME]?.()}
					</div>
				</div>
			</div>
		</div>
		<div class="relative h-10 rounded-tl-[2.5rem] bg-white">
			{#if landingPage.images.length > 0}
				<TokenizedAvatar provider={$tokenProvider} priority={true} size="xxl" src={landingPage.images[0].url} alt={landingPage.images[0].altText} class="absolute -right-4 -mt-38" {mode} />
			{/if}
		</div>
	</div>
	<div class="flex-none bg-white px-4">
		<div class="flex flex-col gap-4 pt-2 pb-0">
			{#if landingPage.supportingText}
				<P class="ellipsis font-medium whitespace-pre-wrap">
					<TokenizedText provider={$tokenProvider} text={landingPage.supportingText} {mode} />
				</P>
			{/if}
			<div class="relative">
				{@render conversion?.()}
				{@render accessory?.[CONVERSION_ANCHOR_NAME]?.()}
			</div>
			<div class="relative">
				{@render section2?.()}
				{@render accessory?.[SECTION_2_ANCHOR_NAME]?.()}
			</div>
		</div>
	</div>
	<div class="flex grow flex-col gap-3 bg-white p-4">
		<div class="relative">
			{@render apps?.()}
		</div>
		{#if accessory?.[APP_ANCHOR_NAME]}
			<div class="relative">
				{@render accessory?.[APP_ANCHOR_NAME]?.()}
			</div>
		{/if}
	</div>
	{@render footer?.(mode)}
</div>
